import { loadNamatAssessment } from '@/lib/firebase/queries/namatAssessment';

export interface UserScore {
  userId: string;
  score: number;
  maxScore: number;
  completedAt: Date;
  periodId: string;
  answers: {
    questionId: string;
    optionId: string;
    score: number;
  }[];
}

// Backed by the Namat scale (spec #86): a questionnaire counts while it is
// valid (filled within its validity), not per calendar quarter.
export async function getUserScore(userId: string): Promise<UserScore | null> {
  try {
    const a = await loadNamatAssessment(userId);
    if (a.status !== 'valid' || !a.scale || !a.filledAt) return null;
    return {
      userId,
      score: a.scale.score,
      maxScore: a.scale.max,
      completedAt: a.filledAt,
      periodId: '',
      answers: []
    };
  } catch (error) {
    console.error('Error getting user score:', error);
    return null;
  }
}

export async function getUserScoreHistory(userId: string): Promise<UserScore[]> {
  const currentScore = await getUserScore(userId);
  return currentScore ? [currentScore] : [];
}

/** A valid Namat questionnaire exists — never "has an exam" (AlMamoon, 2026-09-11; #61). */
export async function hasCompletedQuestionnaire(userId: string): Promise<boolean> {
  try {
    return (await loadNamatAssessment(userId)).status === 'valid';
  } catch (error) {
    console.error('Error checking questionnaire completion:', error);
    return false;
  }
}

/** A new questionnaire can be filled when none is valid (never filled, or expired). */
export async function canCreateNewQuestionnaire(userId: string): Promise<boolean> {
  try {
    return (await loadNamatAssessment(userId)).status !== 'valid';
  } catch (error) {
    console.error('Error checking if a new questionnaire can be filled:', error);
    return false;
  }
}
